import moment from 'moment';
import React, { FC, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import { TaskId } from 'api/models';
import { FieldOption } from 'api/tasks';
import { HumanisedDate } from 'components/atoms/humanised-date';
import { CreatedBy } from 'components/organisms/task-pane/task-detail/task-fields/default-task-fields/created-by';
import {
  Control,
  HorizontallyPaddedContent,
  ProgressBar,
  ProgressBarContainer,
  Select,
} from 'components/organisms/task-pane/task-detail/task-fields/default-task-fields/styled';
import {
  Content,
  FieldGroupLabel,
  FieldGroupRow,
  FieldLabel,
  GhostFields,
  LabelContainer,
  Left,
  Right,
  Row,
} from 'components/organisms/task-pane/task-detail/task-fields/styled';
import { PLACEHOLDER } from 'components/organisms/task-pane/task-detail/task-fields/task-custom-fields/fields/constant';
import { NumberField } from 'components/organisms/tasks/fields';
import { useEffortField, usePriorityField } from 'data/fields/hooks';
import { updateTaskSagaAction } from 'data/tasks/actions';
import { useGetUserFromIndexedDb } from 'hooks/use-idb';
import { EffortDurationField } from './effort-as-duration';

interface DefaultTaskFieldsProps {
  progress?: number;
  effort?: number;
  priority?: FieldOption<number>;
  taskId: TaskId;
  createdBy: number;
  createdAt: number;
  completedAt?: number | null;
  startDateActual?: string;
}

export const DefaultTaskFields: FC<DefaultTaskFieldsProps> = (props) => {
  const {
    effort,
    progress = 0,
    priority,
    createdBy,
    createdAt,
    completedAt,
    taskId: [, projectId, taskId],
    startDateActual,
  } = props;

  const priorityField = usePriorityField();
  const effortField = useEffortField();
  const taskCreatedBy = useGetUserFromIndexedDb(createdBy);

  const dispatch = useDispatch();
  const updateTask = (data) => {
    dispatch(updateTaskSagaAction({ projectId, taskId, data }));
  };

  const updateEffort = (value: number | null) => updateTask({ effort: value });

  const updateProgress = (value: number | string) =>
    updateTask({ progress: Number(value) });

  const [progressValue, setProgressValue] = useState<number>(progress);

  useEffect(() => {
    setProgressValue(progress);
  }, [progress]);

  const [priorityValue, setPriorityValue] =
    useState<FieldOption<number> | undefined>();

  useEffect(() => {
    setPriorityValue(priority);
  }, [priority]);

  return (
    <GhostFields>
      <FieldGroupRow>
        <Left>
          <LabelContainer>
            <FieldGroupLabel>
              <FormattedMessage id="default" />
            </FieldGroupLabel>
          </LabelContainer>
        </Left>
      </FieldGroupRow>
      <Row>
        <Left>
          <LabelContainer>
            <FieldLabel>
              <FormattedMessage id="created_by" />
            </FieldLabel>
          </LabelContainer>
        </Left>
        <Right data-cy="right.content.created_by">
          <Content>
            <HorizontallyPaddedContent>
              {taskCreatedBy ? <CreatedBy user={taskCreatedBy} /> : null}
            </HorizontallyPaddedContent>
          </Content>
        </Right>
      </Row>
      <Row>
        <Left>
          <LabelContainer>
            <FieldLabel>
              <FormattedMessage id="created_on" />
            </FieldLabel>
          </LabelContainer>
        </Left>
        <Right data-cy="right.content.created_on">
          <Content>
            <HorizontallyPaddedContent>
              <LabelContainer>
                <HumanisedDate timestamp={moment(createdAt)} />
              </LabelContainer>
            </HorizontallyPaddedContent>
          </Content>
        </Right>
      </Row>
      <Row>
        <Left>
          <LabelContainer>
            <FieldLabel>
              <FormattedMessage id="start_date_Actual" />
            </FieldLabel>
          </LabelContainer>
        </Left>
        <Right data-cy="right.content.start_date_actual">
          <Content>
            <HorizontallyPaddedContent>
              <LabelContainer>
                {startDateActual ? (
                  <HumanisedDate timestamp={moment(startDateActual)} />
                ) : null}
              </LabelContainer>
            </HorizontallyPaddedContent>
          </Content>
        </Right>
      </Row>
      {completedAt && (
        <Row>
          <Left>
            <LabelContainer>
              <FieldLabel>
                <FormattedMessage id="completed_on" />
              </FieldLabel>
            </LabelContainer>
          </Left>
          <Right data-cy="right.content.completed_on">
            <Content>
              <HorizontallyPaddedContent>
                <LabelContainer>
                  <HumanisedDate timestamp={moment(completedAt)} />
                </LabelContainer>
              </HorizontallyPaddedContent>
            </Content>
          </Right>
        </Row>
      )}
      {effortField && (
        <Row>
          <Left>
            <LabelContainer>
              <FieldLabel>
                <FormattedMessage id="effort" />
              </FieldLabel>
            </LabelContainer>
          </Left>
          <Right data-cy="right.content.effort">
            <Content>
              <EffortDurationField
                onChange={updateEffort}
                field={effortField as unknown as NumberField}
                value={effort}
              />
            </Content>
          </Right>
        </Row>
      )}
      <Row>
        <Left>
          <LabelContainer>
            <FieldLabel>
              <FormattedMessage id="progress" />
            </FieldLabel>
          </LabelContainer>
        </Left>
        <Right data-cy="right.content.progress">
          <Content>
            <ProgressBarContainer>
              <ProgressBar
                value={progressValue}
                onChange={(value) => {
                  setProgressValue(value);
                }}
                onAfterChange={updateProgress}
                onInputPressEnter={(e) => e.currentTarget.blur()}
                onInputBlur={() => updateProgress(progressValue)}
              />
            </ProgressBarContainer>
          </Content>
        </Right>
      </Row>
      {priorityField ? (
        <Row>
          <Left>
            <LabelContainer>
              <FieldLabel>
                <FormattedMessage id="priority" />
              </FieldLabel>
            </LabelContainer>
          </Left>
          <Right data-cy="right.content.priority">
            <Content>
              <Select
                isClearable
                isSearchable={false}
                placeholder={PLACEHOLDER}
                options={priorityField.metaData?.options ?? []}
                type="tag"
                defaultValue={priorityField.metaData?.options?.find(
                  (o) => o.value === priorityField.metaData?.default,
                )}
                value={priorityValue || null}
                onChange={(selected) => {
                  setPriorityValue(selected);
                  updateTask({
                    fields: [
                      {
                        fieldId: priorityField.fieldId,
                        fieldValue: selected ? selected.value : null,
                      },
                    ],
                  });
                }}
                components={{
                  DropdownIndicator: null,
                  IndicatorSeparator: null,
                  Control,
                }}
              />
            </Content>
          </Right>
        </Row>
      ) : null}
    </GhostFields>
  );
};
